import React, { useEffect, useState } from 'react';
import { useLocation, Location, Navigate } from 'react-router';
import { useAuthorizationState } from './useAuthorizationState';
import { OIDCClient } from 'infrastructure/oidc/OIDCClient';
import { useSessionStorageForOIDCTokenSet } from './useSessionStorageForOIDCTokenSet';
import { cancellable } from 'services/utility/cancellable';

function parseAuthCallback(location: Location | undefined) {
    const params = new URLSearchParams(location?.search);
    return new Map(params.entries());
}

export type AuthorizationCallbackProps = {
    oidcClient: OIDCClient;
}

export const AuthorizationCallback = (props: AuthorizationCallbackProps) => {
    const { authState } = useAuthorizationState();
    const { oidcClient } = props;

    const location = useLocation();

    const [forwardUrl, setForwardUrl] = useState<string>();
    const { storeTokenSet } = useSessionStorageForOIDCTokenSet();

    const [message, setMessage] = useState<string>();

    useEffect(() => {
        if (!authState) {
            console.error('unexpected auth callback');
            return;
        }
        if (authState.type !== 'AUTH') return;

        const params = parseAuthCallback(location);

        const error = params.get('error');
        if (error) {
            const uri = params.get('error_uri');
            if (uri) {
                window.location.href = uri;
                return;
            }
            const description = params.get('error_description') ?? `error: ${error}`;
            setMessage(description);
            return;
        }

        const state = params.get('state');
        if (state != authState.state) {
            console.error('unknown state');
            setMessage('error: unknown state');
            return;
        }
        const code = params.get('code');
        if (!code) {
            console.error('missing code');
            setMessage('error: missing code');
            return;
        }

        return cancellable(async isCancel => {
            await oidcClient.getToken(code, authState.codeVerifier);
            if (isCancel()) return;
            setForwardUrl(authState.forwardUrl);
        });
    }, [authState, location, oidcClient]);

    useEffect(() => {
        if (!authState) {
            console.error('unexpected auth callback');
            return;
        }
        if (authState.type !== 'END_SESSION') return;

        const params = parseAuthCallback(location);

        const error = params.get('error');
        if (error) {
            const uri = params.get('error_uri');
            if (uri) {
                window.location.href = uri;
                return;
            }
            const description = params.get('error_description') ?? `error: ${error}`;
            setMessage(description);
            return;
        }

        const state = params.get('state');
        if (state != authState.state) {
            console.error('unknown state');
            setMessage('error: unknown state');
            return;
        }

        storeTokenSet(null);
        oidcClient.clearTokenSet();
        window.location.href = authState.forwardUrl;
    }, [authState, location, oidcClient, storeTokenSet]);

    return (
        forwardUrl
            ? <Navigate to={forwardUrl} />
            : <div>{message ?? 'waiting...'}</div>
    );
};
